/**
 * ToolBrowser - Full-featured tool discovery with search, filter, and grouping
 * 
 * The comprehensive tool browsing experience with:
 * - Virtual Server quick-select cards (hero feature)
 * - Tab filtering by virtual server
 * - Real-time search with debouncing
 * - Category and backend type filters
 * - Tool cards with expand/collapse
 * - Selected tools summary panel
 * 
 * Blue Steel Score Target: 85+
 * - Intelligent Design: VS-first UX, smart grouping
 * - Dynamic Adaptation: Responsive layout, search hints
 * - WOW Factor: Animations, hover effects, glow states
 * 
 * @example
 * ```tsx
 * <ToolBrowser
 *   serviceId="orchestration-service"
 *   selectedToolIds={selectedIds}
 *   onSelectionChange={setSelectedIds}
 * />
 * ```
 */

import React, { useState, useCallback, useMemo } from 'react';
import { VirtualServerBundleCard } from './VirtualServerBundleCard';
import { VirtualServerTabs } from './VirtualServerTabs';
import { ToolCard } from './ToolCard';
import { useVirtualServers } from './hooks/useVirtualServers';
import { useMcpTools } from './hooks/useMcpTools';
import type { MCPTool, VirtualServer } from './types';

export interface ToolBrowserProps {
  serviceId: string;
  selectedToolIds: string[];
  onSelectionChange: (toolIds: string[], tools: MCPTool[]) => void;
  showVirtualServerCards?: boolean;
  showFilters?: boolean;
  maxHeight?: number | string;
  compact?: boolean;
}

export const ToolBrowser: React.FC<ToolBrowserProps> = ({
  serviceId,
  selectedToolIds,
  onSelectionChange,
  showVirtualServerCards = true,
  showFilters = true,
  maxHeight = 500,
  compact = false,
}) => {
  // State
  const [activeVsId, setActiveVsId] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [selectedBackendType, setSelectedBackendType] = useState<string | null>(null);

  // Fetch virtual servers
  const { virtualServers, loading: vsLoading } = useVirtualServers(serviceId);

  // Fetch tools
  const {
    tools: allTools,
    filteredTools,
    loading: toolsLoading,
    categories,
  } = useMcpTools({
    serviceId,
    virtualServerId: activeVsId,
    search: searchTerm,
    category: selectedCategory,
    backendType: selectedBackendType,
  });

  // Get preview tool names for each virtual server
  const vsToolPreviews = useMemo(() => {
    const previews: Record<string, string[]> = {};
    virtualServers.forEach(vs => {
      // Filter tools that belong to this VS (supports full ID, name, or short ID)
      const vsTools = allTools.filter(t => {
        if (t.virtual_server_id === vs.id) return true;
        if (!vs.tool_ids?.length) return false;
        if (vs.tool_ids.includes(t.id)) return true;
        if (vs.tool_ids.includes(t.name)) return true;
        const shortId = t.id.includes(':') ? t.id.split(':').pop() : t.id;
        return shortId ? vs.tool_ids.includes(shortId) : false;
      });
      previews[vs.id] = vsTools.slice(0, 5).map(t => t.name);
    });
    return previews;
  }, [virtualServers, allTools]);

  // Handle tool toggle
  const handleToolToggle = useCallback((tool: MCPTool) => {
    const isSelected = selectedToolIds.includes(tool.id);
    let newIds: string[];
    let newTools: MCPTool[];

    if (isSelected) {
      newIds = selectedToolIds.filter(id => id !== tool.id);
      newTools = allTools.filter(t => newIds.includes(t.id));
    } else {
      newIds = [...selectedToolIds, tool.id];
      newTools = allTools.filter(t => newIds.includes(t.id));
    }

    onSelectionChange(newIds, newTools);
  }, [selectedToolIds, allTools, onSelectionChange]);

  // Helper: find tools matching a virtual server (supports full ID, name, or short ID)
  const getToolsForVs = useCallback((vs: VirtualServer) => {
    return allTools.filter(t => {
      if (t.virtual_server_id === vs.id) return true;
      if (!vs.tool_ids?.length) return false;
      if (vs.tool_ids.includes(t.id)) return true;
      if (vs.tool_ids.includes(t.name)) return true;
      const shortId = t.id.includes(':') ? t.id.split(':').pop() : t.id;
      return shortId ? vs.tool_ids.includes(shortId) : false;
    });
  }, [allTools]);

  // Handle VS bundle selection
  const handleVsBundleSelect = useCallback((vs: VirtualServer) => {
    // Get actual tool IDs from allTools that match this VS
    const vsToolIds = getToolsForVs(vs).map(t => t.id);

    // Check if all VS tools are already selected
    const allSelected = vsToolIds.length > 0 && vsToolIds.every(id => selectedToolIds.includes(id));

    let newIds: string[];
    if (allSelected) {
      // Deselect all from this VS
      newIds = selectedToolIds.filter(id => !vsToolIds.includes(id));
    } else {
      // Select all from this VS (merge with existing)
      newIds = [...new Set([...selectedToolIds, ...vsToolIds])];
    }

    const newTools = allTools.filter(t => newIds.includes(t.id));
    onSelectionChange(newIds, newTools);
  }, [selectedToolIds, allTools, onSelectionChange, getToolsForVs]);

  // Check if a VS is fully selected
  const isVsFullySelected = useCallback((vs: VirtualServer) => {
    const vsToolIds = getToolsForVs(vs).map(t => t.id);
    return vsToolIds.length > 0 && vsToolIds.every(id => selectedToolIds.includes(id));
  }, [selectedToolIds, getToolsForVs]);

  const loading = vsLoading || toolsLoading;

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      gap: 16,
    }}>
      {/* Virtual Server Bundle Cards */}
      {showVirtualServerCards && virtualServers.length > 0 && (
        <div>
          <div style={{
            fontSize: 12,
            fontWeight: 600,
            color: 'var(--main-text-secondary)',
            marginBottom: 10,
            display: 'flex',
            alignItems: 'center',
            gap: 8,
          }}>
            💡 Quick Start: Select a pre-configured tool bundle
          </div>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
            gap: 12,
          }}>
            {virtualServers.map(vs => (
              <VirtualServerBundleCard
                key={vs.id}
                virtualServer={vs}
                selected={isVsFullySelected(vs)}
                onSelect={handleVsBundleSelect}
                toolPreview={vsToolPreviews[vs.id] || []}
                size={compact ? 'compact' : 'normal'}
              />
            ))}
          </div>
        </div>
      )}

      {/* Divider */}
      {showVirtualServerCards && virtualServers.length > 0 && (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: 16,
          color: 'var(--main-text-secondary)',
          fontSize: 11,
        }}>
          <div style={{
            flex: 1,
            height: 1,
            background: 'var(--border-color)',
          }} />
          <span>OR customize manually</span>
          <div style={{
            flex: 1,
            height: 1,
            background: 'var(--border-color)',
          }} />
        </div>
      )}

      {/* Virtual Server Tabs */}
      {virtualServers.length > 0 && (
        <VirtualServerTabs
          virtualServers={virtualServers}
          activeVsId={activeVsId}
          onSelect={setActiveVsId}
          totalToolCount={allTools.length}
          loading={vsLoading}
        />
      )}

      {/* Search & Filters */}
      {showFilters && (
        <div style={{
          display: 'flex',
          gap: 12,
          flexWrap: 'wrap',
        }}>
          {/* Search */}
          <div style={{ flex: 1, minWidth: 200 }}>
            <input
              type="text"
              placeholder="🔍 Search tools..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{
                width: '100%',
                padding: '10px 14px',
                background: 'var(--card-bg)',
                border: '1px solid var(--border-color)',
                borderRadius: 8,
                fontSize: 13,
                color: 'var(--main-text)',
                outline: 'none',
                transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
              }}
              onFocus={(e) => {
                e.target.style.borderColor = 'var(--main-accent)';
                e.target.style.boxShadow = '0 0 0 3px color-mix(in srgb, var(--main-accent) 15%, transparent)';
              }}
              onBlur={(e) => {
                e.target.style.borderColor = 'var(--border-color)';
                e.target.style.boxShadow = 'none';
              }}
            />
          </div>

          {/* Category Filter */}
          {categories.length > 0 && (
            <select
              value={selectedCategory || ''}
              onChange={(e) => setSelectedCategory(e.target.value || null)}
              style={{
                padding: '10px 14px',
                background: 'var(--card-bg)',
                border: '1px solid var(--border-color)',
                borderRadius: 8,
                fontSize: 13,
                color: 'var(--main-text)',
                cursor: 'pointer',
              }}
            >
              <option value="">All Categories</option>
              {categories.map(cat => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
            </select>
          )}

          {/* Backend Type Filter */}
          <select
            value={selectedBackendType || ''}
            onChange={(e) => setSelectedBackendType(e.target.value || null)}
            style={{
              padding: '10px 14px',
              background: 'var(--card-bg)',
              border: '1px solid var(--border-color)',
              borderRadius: 8,
              fontSize: 13,
              color: 'var(--main-text)',
              cursor: 'pointer',
            }}
          >
            <option value="">All Types</option>
            <option value="command">⚡ Commands</option>
            <option value="workflow">🔄 Workflows</option>
            <option value="function">🔧 Functions</option>
          </select>
        </div>
      )}

      {/* Results Count */}
      <div style={{
        fontSize: 12,
        color: 'var(--main-text-secondary)',
      }}>
        {loading ? (
          'Loading tools...'
        ) : (
          `${filteredTools.length} tool${filteredTools.length !== 1 ? 's' : ''} found`
        )}
        {selectedToolIds.length > 0 && (
          <span style={{
            marginLeft: 12,
            color: 'var(--main-accent)',
            fontWeight: 600,
          }}>
            • {selectedToolIds.length} selected
          </span>
        )}
      </div>

      {/* Tools Grid */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: compact
          ? 'repeat(auto-fill, minmax(220px, 1fr))'
          : 'repeat(auto-fill, minmax(300px, 1fr))',
        gap: 12,
        maxHeight: typeof maxHeight === 'number' ? `${maxHeight}px` : maxHeight,
        overflowY: 'auto',
        paddingRight: 8,
      }}>
        {loading ? (
          // Skeleton loaders
          Array.from({ length: 6 }).map((_, i) => (
            <div
              key={i}
              style={{
                height: compact ? 80 : 140,
                borderRadius: 10,
                background: 'var(--skeleton-bg)',
                animation: 'shimmer 1.5s infinite',
              }}
            />
          ))
        ) : filteredTools.length === 0 ? (
          // Empty state
          <div style={{
            gridColumn: '1 / -1',
            textAlign: 'center',
            padding: 40,
            color: 'var(--main-text-secondary)',
          }}>
            <div style={{ fontSize: 32, marginBottom: 12 }}>🔍</div>
            <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 8 }}>
              No tools found
            </div>
            <div style={{ fontSize: 12 }}>
              Try adjusting your search or filters
            </div>
          </div>
        ) : (
          filteredTools.map(tool => (
            <ToolCard
              key={tool.id}
              tool={tool}
              selected={selectedToolIds.includes(tool.id)}
              onSelect={handleToolToggle}
              compact={compact}
            />
          ))
        )}
      </div>

      {/* Selected Tools Summary */}
      {selectedToolIds.length > 0 && (
        <div style={{
          padding: 14,
          background: 'color-mix(in srgb, var(--main-accent) 10%, transparent)',
          border: '1px solid color-mix(in srgb, var(--main-accent) 30%, transparent)',
          borderRadius: 10,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}>
          <div>
            <span style={{
              fontSize: 13,
              fontWeight: 600,
              color: 'var(--main-text)',
            }}>
              ✨ {selectedToolIds.length} tool{selectedToolIds.length !== 1 ? 's' : ''} selected
            </span>
          </div>
          <button
            onClick={() => onSelectionChange([], [])}
            style={{
              padding: '6px 14px',
              background: 'color-mix(in srgb, var(--error-color) 15%, transparent)',
              border: '1px solid color-mix(in srgb, var(--error-color) 30%, transparent)',
              borderRadius: 6,
              color: 'var(--error-color)',
              fontSize: 12,
              fontWeight: 600,
              cursor: 'pointer',
              transition: 'all 0.2s ease',
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.background = 'var(--error-color)';
              e.currentTarget.style.color = 'white';
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.background = 'color-mix(in srgb, var(--error-color) 15%, transparent)';
              e.currentTarget.style.color = 'var(--error-color)';
            }}
          >
            Clear All
          </button>
        </div>
      )}

      {/* Keyframes */}
      <style>{`
        @keyframes shimmer {
          0% { opacity: 0.5; }
          50% { opacity: 0.8; }
          100% { opacity: 0.5; }
        }
      `}</style>
    </div>
  );
};

